import { Key } from "lucide-react";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge,
  Diagram, MockField, MockButton,
} from "../shared";

export default function FormsPasswordSection() {
  return (
    <div data-testid="guide-forms-password">
      <PageHeading
        icon={Key}
        title="Change / Reset Password"
        description="Two ways to sort out your password: change it while you're logged in, or reset it by email if you've forgotten it. Admins can also reset another user's password."
      />

      <Callout variant="info" title="In plain words">
        If you <span className="text-white">know</span> your password and want a new one, use "change". If you've <span className="text-white">forgotten</span> it, use "forgot password" on the login page and the system emails you a link.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>Change: routine update, or after sharing a temporary password.</li>
          <li>Forgot: you can't log in because you don't remember your password.</li>
          <li>Admin reset: you're an admin helping a colleague who is locked out.</li>
        </ul>
      </Section>

      <Section title="Change your own password (logged in)">
        <Diagram
          frameLabel="My Account → Password"
          legend={[
            <>Type your <span className="text-white">current password</span>.</>,
            <>Type your <span className="text-white">new password</span>.</>,
            <>Type the new password again to <span className="text-white">confirm</span>.</>,
            <>Click <span className="text-white">Update Password</span>.</>,
          ]}
        >
          <MockField label="Current password" placeholder="••••••••" marker={1} />
          <MockField label="New password" placeholder="••••••••" marker={2} />
          <MockField label="Confirm new password" placeholder="••••••••" marker={3} />
          <MockButton label="Update Password" marker={4} />
        </Diagram>
        <Steps
          steps={[
            <>Click your username in the header → <span className="text-white">My Account</span>, then open the <span className="text-white">Password</span> tab.</>,
            <>Type your <span className="text-white">current password</span>.</>,
            <>Type your <span className="text-white">new password</span>, then type it again to confirm.</>,
            <>Click <span className="text-white">Update Password</span>.</>,
          ]}
        />

        <Field name="Current password" required="required">
          <p>Your existing password. This proves it's really you making the change.</p>
        </Field>
        <Field name="New password" required="required">
          <p>The replacement password. Minimum length and complexity follow your IT Admin's policy.</p>
        </Field>
        <Field name="Confirm new password" required="required">
          <p>Type the new password again. It must match exactly — this catches typos.</p>
        </Field>

        <NextStep>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Your password updates immediately (it's securely scrambled — the system never stores it as plain text).</li>
            <li>Your current session keeps working — you don't need to log back in.</li>
            <li>Other open sessions for your account stay valid until they expire (8h).</li>
            <li>The change is recorded in the log.</li>
          </ul>
        </NextStep>
      </Section>

      <Section title="Forgot password (not logged in)">
        <Diagram
          frameLabel="Login → Forgot password?"
          legend={[
            <>Enter your <span className="text-white">username</span> (not your email).</>,
            <>Click <span className="text-white">Send reset link</span> — then check your inbox.</>,
          ]}
        >
          <MockField label="Username" placeholder="amal.r" marker={1} />
          <MockButton label="Send reset link" marker={2} />
        </Diagram>
        <Steps
          steps={[
            <>On the login page, click <span className="text-white">Forgot password?</span> and enter your <span className="text-white">username</span> (not email — the system finds the email from your account).</>,
            <>Submit. For privacy, you see the same confirmation message whether or not the username exists.</>,
            <>Check your inbox for the email titled <span className="text-white">"Password reset request"</span>. The link works for <span className="text-white">30 minutes</span>. Click it, set your new password, and submit — then sign in normally.</>,
          ]}
        />

        <Callout variant="warning" title="Only the latest link works">
          If you click "Forgot password?" twice, the first link stops working. Always use the most recent email.
        </Callout>

        <Callout variant="info" title="If no email arrives">
          Two possible reasons: your account has no email address on file (ask an admin), or email sending isn't set up for the system (ask the IT Admin). The reset needs both to work.
        </Callout>
      </Section>

      <Section title="Admin: reset another user's password">
        <div className="flex items-center gap-2 mb-2">
          <RoleBadge role="admin" />
        </div>
        <Diagram
          frameLabel="Admin → User Accounts"
          legend={[
            <>Find the user and click <span className="text-white">Reset Password</span>.</>,
            <>Confirm with <span className="text-white">your own password</span>.</>,
            <>Set a <span className="text-white">temporary password</span> to share with them.</>,
          ]}
        >
          <MockButton label="Reset Password" marker={1} />
          <MockField label="Your password (confirm it's you)" placeholder="••••••••" marker={2} />
          <MockField label="Temporary password" placeholder="••••••••" marker={3} />
        </Diagram>
        <Steps
          steps={[
            <>Open <span className="text-white">Admin → User Accounts</span>, find the user, and click <span className="text-white">Reset Password</span>.</>,
            <>The system asks for <span className="text-white">your own password</span> as confirmation (a quick re-check that it's really you).</>,
            <>Set a <span className="text-white">temporary password</span>, share it with the user another way (not in the system), and ask them to change it on first login.</>,
          ]}
        />
        <Callout variant="warning">
          You cannot reset the password of a user at your own role level or above. To reset another IT Admin's password you need to be a Super Admin (and so on up the ladder).
        </Callout>
      </Section>
    </div>
  );
}
